import type { RelatedEntityType } from "@dune/types";
import { useEffect } from "react";
import { useDocumentStore } from "../../stores/document.store";
import type { DocumentPreviewPanelProps } from "../../types/panel.types";
import { getEntityPanelComponent } from "../../utils/panel-registry";
import { usePanel } from "../usePanel";

interface UseDocumentSelectionProps {
	entityType: RelatedEntityType;
	entityId: number;
	customComponent?: React.ComponentType<DocumentPreviewPanelProps>;
	onDelete?: (documentId: string) => void;
}

export const useDocumentSelection = ({
	entityType,
	entityId,
	customComponent,
	onDelete,
}: UseDocumentSelectionProps) => {
	const panel = usePanel();
	const selectedDocumentUrl = useDocumentStore(
		(state) => state.selectedDocumentUrl,
	);
	const selectedDocumentId = useDocumentStore(
		(state) => state.selectedDocumentId,
	);
	const documentMimeType = useDocumentStore((state) => state.documentMimeType);
	const documentName = useDocumentStore((state) => state.documentName);
	const clearDocument = useDocumentStore((state) => state.clearDocument);

	const DocumentPreviewComponent =
		customComponent || getEntityPanelComponent(entityType, "DocumentPreview");

	useEffect(() => {
		if (!selectedDocumentUrl && !selectedDocumentId) return;

		if (DocumentPreviewComponent) {
			panel.showCustomContent({
				component: (
					<DocumentPreviewComponent
						entityId={entityId}
						entityType={entityType}
						url={selectedDocumentUrl || ""}
						documentId={selectedDocumentId}
						title={documentName || "Aperçu du document"}
						mimeType={documentMimeType}
						fileName={documentName}
						onDelete={onDelete}
					/>
				),
				meta: {
					title: documentName || "Aperçu du document",
					onClose: () => {
						clearDocument();
						panel.clearPanel();
					},
				},
			});
		} else {
			panel.showDocumentPreview({
				url: selectedDocumentUrl || "",
				documentId: selectedDocumentId,
				title: documentName || "Aperçu du document",
				mimeType: documentMimeType,
				fileName: documentName,
				onDelete: onDelete,
				onClose: () => {
					clearDocument();
					panel.clearPanel();
				},
			});
		}
	}, [
		selectedDocumentUrl,
		selectedDocumentId,
		documentMimeType,
		documentName,
		entityId,
		entityType,
		panel,
		DocumentPreviewComponent,
		clearDocument,
		onDelete,
	]);

	return {
		selectedDocumentUrl,
		clearDocument,
	};
};
